import React, { useCallback, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import Grid from "@material-ui/core/Grid";
import LinearProgress from "@material-ui/core/LinearProgress";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { Typography } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import { makeStyles } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";
import Button from "@material-ui/core/Button";
import { getAuthInfo } from "../../../../Store/api.slice";
import { getBillingProfileIdValue } from "../../../../Store/purchase.slice"
import { setAchDisabled, setSubscriptionId, setTaxExempt } from "../../../../Store/purchase.slice"
import { createApolloFetch } from "apollo-fetch";
const { REACT_APP_API_URL, REACT_APP_API_URL_LOCAL } = process.env;
const apiUrl = REACT_APP_API_URL && REACT_APP_API_URL.startsWith('http') ? REACT_APP_API_URL : REACT_APP_API_URL_LOCAL || '';

const useStyles = makeStyles(theme => ({
    subscriptionError: { width: '100%' },
    subscriptionOption: {
        margin: 0,
        padding: 0,
        display: "flex",
        alignItems: "stretch",
        justifyContent: "flex-start",
        borderTop: '1px solid black',
        borderLeft: '1px solid black',
        borderRight: '1px solid black',
        '&:first-child': {
            borderTopLeftRadius: '3px',
            borderTopRightRadius: '3px'
        },
        '&:last-child': {
            borderBottom: '1px solid black',
            borderBottomLeftRadius: '3px',
            borderBottomRightRadius: '3px'
        },
        '&:hover, &.Mui-selected': {
            border: `1px solid ${theme.palette.primary.main}`,
            '& + $subscriptionOption': { borderTop: 0 },
            '& > $subscriptionInfoCost': {
                backgroundColor: theme.palette.primary.main,
                '& > .MuiTypography-colorTextPrimary': { color: 'white' },
            }
        }
    },
    subscriptionInfoText: {
        margin: 0,
        width: '80%',
        overflowWrap: "break-word",
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    }
}))

//Subscriptions error
export function SubscriptionPage() {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useDispatch();
    const authInfo = useSelector(getAuthInfo);
    const billingProfileId = useSelector(getBillingProfileIdValue);
    const [subscriptionsError, setSubscriptionsError] = useState(null);
    const [subscriptions, setSubscriptions] = useState([]);
    const [selectedNewSubscription, setSelectedNewSubscription] = useState(false);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const nextPage = useCallback(() => {
        history.push('/payor');
    }, [history]);

    const setActiveNewSubscription = useCallback(() => {
        dispatch(setSubscriptionId(null));
        dispatch(setTaxExempt(false));
        dispatch(setAchDisabled(false));
        setSelectedSubscription(null);
        setSelectedNewSubscription(true);
    }, [dispatch]);

    const handleNoSubscriptions = useCallback(() => {
        setActiveNewSubscription();
        nextPage();
    }, [nextPage, setActiveNewSubscription]);

    useEffect(() => {
        async function fetchData() {
            setIsLoading(true)
            const graphQL = createApolloFetch({ uri: `${apiUrl}/graphql` });
            graphQL.use(({ options }, next) => {
                options.headers = options.headers || {};
                options.headers['Authorization'] = `Bearer ${authInfo.token}`;
                next();
            });
            const filters = [
                {
                    model: 'PAYOR',
                    field: 'cognito_sub',
                    op: 'EQ',
                    value: authInfo.id,
                },
                {
                    model: 'BILLING_PROFILE',
                    field: 'validation_only',
                    op: 'EQ',
                    value: false
                },
                {
                    model: 'BILLING_PROFILE',
                    field: 'id',
                    op: 'EQ',
                    value: billingProfileId,
                }
            ]

            const query =
                `
                query listSubscriptions($filters: [Filter]) {
                    listSubscriptions(filters: $filters) {
                        subscriptions {
                            id
                            achDisabled
                            subscription_name
                            tax_exempt
                            billing_profile {
                                id
                                name
                            }
                            payor {
                                id
                                cognito_sub
                            }
                            leasingRights {
                                id
                            }
                            leaseSubtenants {
                                type
                            }
                        }
                    }
                }
                `;
            const variables = { filters }
            const { errors, data } = await graphQL({ query, variables })

            if (errors) {
                setSubscriptionsError(errors)
            }

            const allSubscriptions = data.listSubscriptions.subscriptions;
            const nonLeasedSubscriptions = allSubscriptions.filter(({ leasingRights, leaseSubtenants }) => (
                !leasingRights.length &&
                !leaseSubtenants.some(({ type }) => type === 'CHILD')
            ));

            if (!nonLeasedSubscriptions.length) return handleNoSubscriptions();

            setSubscriptions(nonLeasedSubscriptions);
            setIsLoading(false);
        }

        fetchData();

    }, [authInfo.id, authInfo.token, billingProfileId, handleNoSubscriptions]);

    const setActiveSubscription = (index, id) => {
        dispatch(setSubscriptionId(id));
        dispatch(setTaxExempt(subscriptions[index].tax_exempt));
        dispatch(setAchDisabled(subscriptions[index].achDisabled));
        setSelectedSubscription(index);
        setSelectedNewSubscription(null);
    };

    return (
        <Grid container>
            <Grid item xs={12}>
                <LinearProgress variant="determinate" value={85} />
            </Grid>
            <Grid item xs={12} md={7}>
                <Box m={3}>
                    <Box py={1}>
                        <Typography variant='h5'>Select Your Subscription</Typography>
                    </Box>
                    <List className={classes.subscriptionSelect}>
                        {isLoading && <ListItem key='loading'>Loading...</ListItem>}
                        {subscriptions && subscriptions.map(({ id, subscription_name }, index) => (
                            <ListItem
                                key={id}
                                className={classes.subscriptionOption}
                                selected={index === selectedSubscription}
                                onClick={() => { setActiveSubscription(index, id); }}
                                button
                            >
                                <ListItemText
                                    className={classes.subscriptionInfoText}
                                    primary={subscription_name}
                                    primaryTypographyProps={{ variant: 'body1', "data-cy": `subscription-name-text-${index}` }}
                                    secondaryTypographyProps={{"data-cy" : `subscription-description-text-${index}`}}
                                />
                            </ListItem>
                        ))}
                        <ListItem
                            className={classes.subscriptionOption}
                            selected={selectedNewSubscription}
                            onClick={() => { setActiveNewSubscription(); }}
                            button
                        >
                            <ListItemText
                                className={classes.subscriptionInfoText}
                                primary={"Create New Subscription"}
                                primaryTypographyProps={{ variant: 'body1', "data-cy": "create-new-subscription-text" }}
                            />
                        </ListItem>
                        {subscriptionsError ? <ListItem key='error'>
                            <Alert severity='error' width={1}>Failed to fetch Subscriptions</Alert>
                        </ListItem> : ''}
                    </List>
                </Box>
            </Grid>
            <Grid container item xs={12} justifyContent="space-between">
                <Box m={3}>
                    <Button
                        size="large"
                        variant="contained"
                        color="secondary"
                        onClick={() => history.goBack()}
                        data-cy="back-button"
                    >
                        Back
                    </Button>
                </Box>
                <Box m={3}>
                    <Button
                        size="large"
                        variant="contained"
                        color="primary"
                        onClick={nextPage}
                        data-cy="submit-button"
                    >
                        Next
                    </Button>
                </Box>
            </Grid>
        </Grid>
    )
}

export default React.memo(SubscriptionPage);
